import { useEffect, useRef, RefObject } from 'react';
import SharePlaylist from './SharePlaylist';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../contexts/useDarkMode';

interface MenuDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: RefObject<HTMLButtonElement>;
  isPlaylistPage: boolean;
  playlistId: string | undefined;
}

export default function MenuDropdown({
  isOpen,
  onClose,
  buttonRef,
  isPlaylistPage,
  playlistId,
}: MenuDropdownProps) {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-full z-50 mt-2 w-32 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
    >
      <div className="py-2" role="none">
        {/* Theme Buttons */}
        <div className="flex items-center justify-center gap-2 px-3 py-2">
          <button
            onClick={() => {
              if (darkMode) {
                toggleDarkMode();
              }
              onClose();
            }}
            className={`rounded-md px-2 py-1.5 transition-colors duration-200 ${!darkMode ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'}`}
            role="menuitem"
            aria-label="Light mode"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </button>
          <button
            onClick={() => {
              if (!darkMode) {
                toggleDarkMode();
              }
              onClose();
            }}
            className={`rounded-md px-2 py-1.5 transition-colors duration-200 ${darkMode ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'}`}
            role="menuitem"
            aria-label="Dark mode"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          </button>
        </div>

        {/* Conditional Share Item */}
        {isPlaylistPage && playlistId && (
          <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700" role="none">
            <div
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onClose();
                }
              }}
              role="button"
              tabIndex={0}
              className="flex min-h-[44px] items-center"
            >
              <SharePlaylist playlistId={playlistId} />
            </div>
          </div>
        )}

        {/* Links */}
        <div className="flex items-center justify-center gap-2 px-3 py-2">
          <Link to="/about" className="dropdown-link-class" onClick={onClose}>
            About
          </Link>
        </div>
        <div className="flex items-center justify-center gap-2 px-3 py-2">
          <Link to="/privacy" className="dropdown-link-class" onClick={onClose}>
            Privacy
          </Link>
        </div>
      </div>
    </div>
  );
}
